import { Pagination } from "services/search";
import {
  FleetVehicleExpense,
  FleetVehicleExpenseEditFormValues,
  FleetVehicleExpenseRow,
  FleetVehicleExpenseStats,  FleetVehicleExpenseUpload,
} from "./types";
import { DataWithPagination } from "services/api/types";
import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import {
  transformFleetVehicleExpense,
  transformFleetVehicleExpenseOnCreate,
  transformFleetVehicleExpenseOnUpdate,
  transformFleetVehicleExpenseEditFormValues,
  transformFleetVehicleExpenseRow,} from "./transformations";
import { FleetDocumentUpload } from "../documents/types";
import { transformErrorResponse } from "services/api/transformation";

/*---------------------------------------------------------------------------------------
  SEARCH
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleExpensesAPI = async (
  pagination: Pagination,
  filters?: {
    vehicleId?: string | null;
    search?: string | null;
    expenseTypeId?: string | null;
    officeId?: string | null;
    /** true = samo opći troškovi tvrtke (bez vozila). */
    withoutVehicle?: boolean | null;
    expenseDateFrom?: Date | null;
    expenseDateTo?: Date | null;
    amountMin?: number | null;
    amountMax?: number | null;
    vehicleManufacturerId?: string | null;
    vehicleModelId?: string | null;
    vehicleTypeId?: string | null;
  }
): Promise<DataWithPagination<FleetVehicleExpenseRow>> => {
  let path = `/v1/fleet/vehicles/expenses?`;

  if (filters?.vehicleId) path += `vehicleId=${filters.vehicleId}&`;
  if (filters?.search) path += `name=${encodeURIComponent(filters.search)}&`;
  if (filters?.expenseTypeId) path += `expenseTypeId=${filters.expenseTypeId}&`;
  if (filters?.expenseTypeId) path += `expenseTypeId=${filters.expenseTypeId}&`;
  if (filters?.officeId) path += `officeId=${filters.officeId}&`;
  if (filters?.withoutVehicle != null) path += `withoutVehicle=${filters.withoutVehicle}&`;
  if (filters?.expenseDateFrom) path += `expenseDateFrom=${filters.expenseDateFrom.toISOString()}&`;
  if (filters?.expenseDateTo) path += `expenseDateTo=${filters.expenseDateTo.toISOString()}&`;
  if (filters?.amountMin) path += `amountMin=${filters.amountMin}&`;
  if (filters?.amountMax) path += `amountMax=${filters.amountMax}&`;
  if (filters?.vehicleManufacturerId) path += `vehicleManufacturerId=${filters.vehicleManufacturerId}&`;
  if (filters?.vehicleModelId) path += `vehicleModelId=${filters.vehicleModelId}&`;
  if (filters?.vehicleTypeId) path += `vehicleTypeId=${filters.vehicleTypeId}&`;

  return await paginatedApiCall(
    path,
    pagination,
    transformFleetVehicleExpenseRow
  );
};


/*---------------------------------------------------------------------------------------
  INFO
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleExpenseAPI = async (
  id: string
): Promise<FleetVehicleExpense> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/expenses/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetVehicleExpense(response.data);
};

/*---------------------------------------------------------------------------------------
  CREATE
-----------------------------------------------------------------------------------------*/
export const createFleetVehicleExpenseAPI = async (
  vehicleId: string,
  formValues: FleetVehicleExpenseEditFormValues,
  uploads: FleetDocumentUpload [],
  t: (key: string) => string
): Promise<FleetVehicleExpense> => {
  const response = await createApiCall(
    {
      path: "/v1/fleet/vehicles/expenses/",
      method: "POST",
      body: transformFleetVehicleExpenseOnCreate(
        vehicleId,
        formValues,
        uploads
      ),
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.expense.create.message.success"),
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetVehicleExpense(response.data);
};

/*---------------------------------------------------------------------------------------
  EDIT
-----------------------------------------------------------------------------------------*/
export const updateFleetVehicleExpenseAPI = async (
  vehicleId: string,
  expenseId: string,
  formValues: FleetVehicleExpenseEditFormValues,
  uploads: FleetDocumentUpload [],
  t: (key: string) => string
): Promise<FleetVehicleExpense> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/expenses/${expenseId}`,
      method: "PUT",
      body: transformFleetVehicleExpenseOnUpdate(vehicleId, formValues, uploads),
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.expense.update.message.success"),
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetVehicleExpense(response.data);
};

/*---------------------------------------------------------------------------------------
    DELETE
-----------------------------------------------------------------------------------------*/
export const deleteFleetVehicleExpenseAPI = async (
  id: string,
  t: (key: string) => string
): Promise<FleetVehicleExpense> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/expenses/${id}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.expense.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};


/*---------------------------------------------------------------------------------------
  VEZA NA PREMJEŠTAJ
-----------------------------------------------------------------------------------------*/
/**
 * Veže trošak uz premještaj vozila. Čarobnjak premještaja troškove unosi postojećim
 * modalom, prije nego premještaj postoji, pa vezu postavlja tek na kraju.
 */
export const setFleetVehicleExpenseLocationAPI = async (
  expenseId: string,
  vehicleLocationId: string | null
): Promise<void> => {
  await createApiCall(
    {
      path: `/v1/fleet/vehicles/expenses/${expenseId}/location`,
      method: "PATCH",
      body: { vehicleLocationId },
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();
};

/*---------------------------------------------------------------------------------------
  STATS
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleExpenseStatsAPI = async (
  vehicleId: string
): Promise<FleetVehicleExpenseStats> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${vehicleId}/expenses/stats`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data as FleetVehicleExpenseStats;
};

/*---------------------------------------------------------------------------------------
  STATS — tenant-wide (Finance > Troškovi > Statistika tab)
  Vraća isti shape kao per-vehicle stats; sellingPriceHistory je prazan jer
  je to per-vehicle koncept koji se ne primjenjuje na cijelu flotu.
-----------------------------------------------------------------------------------------*/
export const getAllVehicleExpenseStatsAPI = async (
  filters?: {
    expenseTypeId?: string | null;
    amountMin?: number | null;
    amountMax?: number | null;
    vehicleManufacturerId?: string | null;
    vehicleModelId?: string | null;
    vehicleTypeId?: string | null;
  }
): Promise<FleetVehicleExpenseStats> => {
  let path = `/v1/fleet/expenses/stats?`;

  if (filters?.expenseTypeId) path += `expenseTypeId=${filters.expenseTypeId}&`;
  if (filters?.amountMin) path += `amountMin=${filters.amountMin}&`;
  if (filters?.amountMax) path += `amountMax=${filters.amountMax}&`;
  if (filters?.vehicleManufacturerId) path += `vehicleManufacturerId=${filters.vehicleManufacturerId}&`;
  if (filters?.vehicleModelId) path += `vehicleModelId=${filters.vehicleModelId}&`;
  if (filters?.vehicleTypeId) path += `vehicleTypeId=${filters.vehicleTypeId}&`;

  const response = await createApiCall(
    {
      path,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data as FleetVehicleExpenseStats;
};




/*---------------------------------------------------------------------------------------
    DELETE
-----------------------------------------------------------------------------------------*/
export const deleteFleetVehicleExpenseUploadAPI = async (
  vehicleExpenseId: string,
  uploadId: string,
  t: (key: string) => string
): Promise<FleetVehicleExpenseUpload> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/expenses/${vehicleExpenseId}/upload/${uploadId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.expense.upload.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};
/*---------------------------------------------------------------------------------------
  EDIT FORM — trošak u obliku obrasca (koristi ga unos općeg troška tvrtke)
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleExpenseEditFormAPI = async (
  id: string
): Promise<FleetVehicleExpenseEditFormValues> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/expenses/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetVehicleExpenseEditFormValues(response.data);
};
